import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { authStore } from '@/store/authStore'
import { incidentesService } from '@/services/incidentesService'
import type { Incidente, SeveridadeIncidente } from '@/services/lgpdService'
import {
  CATEGORIAS_INCIDENTE,
  ESTADOS_INCIDENTE,
  ESTADO_INCIDENTE_LABEL,
  EstadoIncidente,
  IncidenteEvento,
  ResumoIncidentes,
} from '@/types/incidente'
import { tokens } from '@/theme'

const SEVERIDADE_COR: Record<SeveridadeIncidente, string> = {
  BAIXA: tokens.textSecondary,
  MEDIA: tokens.statusWarning,
  ALTA: tokens.brandPrimary,
  CRITICA: tokens.statusDanger,
}

const ESTADO_COR: Record<EstadoIncidente, string> = {
  REPORTADO: tokens.statusDanger,
  TRIAGEM: tokens.statusWarning,
  CONTENCAO: tokens.statusWarning,
  INVESTIGACAO: tokens.brandPrimary,
  RESOLVIDO: tokens.statusSuccess,
  ENCERRADO: tokens.textSecondary,
}

const rotuloCategoria = (codigo?: string | null) =>
  codigo ? CATEGORIAS_INCIDENTE.find((c) => c.value === codigo)?.label ?? codigo : '—'

const estadoDe = (i: Incidente): EstadoIncidente => i.estado ?? 'REPORTADO'

/** Ação por linha, como no plano: o rótulo diz o que o DPO vai fazer ao abrir. */
export const acaoDaFila = (estado: EstadoIncidente): string => {
  switch (estado) {
    case 'REPORTADO':
    case 'TRIAGEM':
      return 'Investigar →'
    case 'CONTENCAO':
    case 'INVESTIGACAO':
      return 'Em tratamento'
    default:
      return 'Concluído'
  }
}

const ChipEstado: FC<{ estado: EstadoIncidente }> = ({ estado }) => (
  <Chip
    size="small"
    label={ESTADO_INCIDENTE_LABEL[estado]}
    sx={{ borderColor: ESTADO_COR[estado], color: ESTADO_COR[estado], fontWeight: 600 }}
    variant="outlined"
  />
)

/**
 * Privacidade e Incidentes (Onda 7.6 / A13) — a fila do DPO.
 *
 * Estados só andam para frente (REPORTADO → TRIAGEM → CONTENÇÃO → INVESTIGAÇÃO →
 * RESOLVIDO → ENCERRADO); a linha do tempo é append-only no banco, então o que aparece
 * no drawer é prova do tratamento, não um resumo editável. Escopo por tenant é do
 * servidor: a tela não filtra nada por conta própria.
 */
export const PrivacidadeIncidentesPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const { enqueueSnackbar } = useSnackbar()
  const isAdmin = useMemo(
    () => !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'),
    [usuario],
  )

  const [filtroEstado, setFiltroEstado] = useState<EstadoIncidente | ''>('')
  const [fila, setFila] = useState<Incidente[] | null>(null)
  const [resumo, setResumo] = useState<ResumoIncidentes | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [aberto, setAberto] = useState<Incidente | null>(null)
  const [linha, setLinha] = useState<IncidenteEvento[] | null>(null)
  const [erroLinha, setErroLinha] = useState<string | null>(null)
  const [para, setPara] = useState<EstadoIncidente | ''>('')
  const [nota, setNota] = useState('')
  const [transicionando, setTransicionando] = useState(false)

  const carregar = useCallback(async () => {
    if (!isAdmin) return
    setCarregando(true)
    setErro(null)
    try {
      const [pagina, kpis] = await Promise.all([
        incidentesService.fila(filtroEstado || undefined),
        incidentesService.resumo(),
      ])
      setFila(pagina.content)
      setResumo(kpis)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar a fila de incidentes')
      setFila(null)
      setResumo(null)
    } finally {
      setCarregando(false)
    }
  }, [isAdmin, filtroEstado])

  useEffect(() => {
    carregar()
  }, [carregar])

  const abrir = async (i: Incidente) => {
    setAberto(i)
    setPara('')
    setNota('')
    setLinha(null)
    setErroLinha(null)
    try {
      setLinha(await incidentesService.linhaDoTempo(i.protocolo))
    } catch (e) {
      setErroLinha(e instanceof Error ? e.message : 'Falha ao carregar a linha do tempo')
    }
  }

  const proximos = (i: Incidente): EstadoIncidente[] => {
    if (i.proximosEstados) return i.proximosEstados
    const idx = ESTADOS_INCIDENTE.indexOf(estadoDe(i))
    return ESTADOS_INCIDENTE.slice(idx + 1)
  }

  const transicionar = async () => {
    if (!aberto || !para) return
    setTransicionando(true)
    try {
      const atualizado = await incidentesService.transicionar(aberto.protocolo, {
        para,
        nota: nota.trim() || undefined,
      })
      enqueueSnackbar(`${aberto.protocolo} → ${ESTADO_INCIDENTE_LABEL[para]}`, {
        variant: 'success',
      })
      setAberto(atualizado)
      setPara('')
      setNota('')
      setLinha(await incidentesService.linhaDoTempo(atualizado.protocolo))
      await carregar()
    } catch (e) {
      const falha = e as { message?: string; status?: number }
      enqueueSnackbar(
        falha.status === 409
          ? 'Transição recusada: a fila só anda para frente. Recarregue e tente de novo.'
          : falha.message ?? 'Falha ao mudar o estado',
        { variant: 'error' }
      )
    } finally {
      setTransicionando(false)
    }
  }

  const cabecalho = (
    <PageHeader
      title="Privacidade e Incidentes"
      eyebrow="Administração"
      subtitle="Fila do DPO — incidentes de segurança com dados pessoais (LGPD Art. 48)"
    />
  )

  if (usuario && !isAdmin) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista
          estado="semPermissao"
          detalhe="A fila de incidentes é restrita ao DPO (administrador)."
        />
      </Box>
    )
  }

  const kpi = (v?: number) => (v === undefined ? '—' : v)

  return (
    <Box>
      {cabecalho}

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="escura"
            label="Abertos"
            carregando={carregando && !resumo}
            valor={kpi(resumo?.abertos)}
            legenda="Reportado a Investigação"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Em tratamento"
            carregando={carregando && !resumo}
            valor={kpi(resumo?.emTratamento)}
            legenda="Triagem, Contenção ou Investigação"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Críticos"
            carregando={carregando && !resumo}
            valor={kpi(resumo?.criticos)}
            legenda="Abertos com severidade crítica"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Encerrados no mês"
            carregando={carregando && !resumo}
            valor={kpi(resumo?.encerradosNoMes)}
            legenda="Encerrados desde o dia 1º"
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Estado"
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value as EstadoIncidente | '')}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {ESTADOS_INCIDENTE.map((e) => (
            <MenuItem key={e} value={e}>
              {ESTADO_INCIDENTE_LABEL[e]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {carregando && !fila ? (
        <EstadoDeLista estado="carregando" />
      ) : erro && !fila ? (
        <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar de novo', onClick: carregar }} />
      ) : fila && fila.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhum incidente na fila"
          detalhe={filtroEstado ? 'Nada neste estado.' : 'Nenhum incidente reportado.'}
        />
      ) : fila ? (
        <TabelaAcao<Incidente>
          aria-label="Fila de incidentes"
          colunas={[
            { titulo: 'Protocolo', render: (i) => <strong>{i.protocolo}</strong> },
            { titulo: 'Tenant', render: (i) => i.tenantId ? i.tenantId.slice(0, 8) : '—' },
            { titulo: 'Categoria', render: (i) => rotuloCategoria(i.categoria) },
            {
              titulo: 'Severidade',
              render: (i) => (
                <Typography variant="body2" sx={{ color: SEVERIDADE_COR[i.severidade], fontWeight: 600 }}>
                  {i.severidade}
                </Typography>
              ),
            },
            { titulo: 'Estado', render: (i) => <ChipEstado estado={estadoDe(i)} /> },
            { titulo: 'Reportado em', render: (i) => new Date(i.criadoEm).toLocaleString('pt-BR') },
          ]}
          linhas={fila}
          chaveDaLinha={(i) => i.id}
          onAbrir={abrir}
          renderAcao={(i) => acaoDaFila(estadoDe(i))}
        />
      ) : null}

      <Drawer anchor="right" open={!!aberto} onClose={() => setAberto(null)}>
        {aberto && (
          <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 3 }} role="dialog" aria-label={aberto.protocolo}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {aberto.protocolo}
              </Typography>
              <IconButton onClick={() => setAberto(null)} aria-label="Fechar">
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <ChipEstado estado={estadoDe(aberto)} />
              <Chip size="small" label={aberto.severidade} sx={{ color: SEVERIDADE_COR[aberto.severidade] }} variant="outlined" />
              {aberto.categoria && <Chip size="small" label={rotuloCategoria(aberto.categoria)} variant="outlined" />}
            </Stack>
            <Typography variant="subtitle1" fontWeight={600}>
              {aberto.titulo}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
              {aberto.descricao}
            </Typography>
            {aberto.anexos && aberto.anexos.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {aberto.anexos.length} anexo(s) no originei-documentos
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Linha do tempo
            </Typography>
            {erroLinha && <Alert severity="error">{erroLinha}</Alert>}
            {!linha && !erroLinha && <CircularProgress size={20} />}
            {linha && (
              <Stack spacing={1} data-testid="linha-do-tempo">
                {linha.map((ev) => (
                  <Box key={ev.id} sx={{ borderLeft: 3, borderColor: ESTADO_COR[ev.para], pl: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {ev.de ? `${ESTADO_INCIDENTE_LABEL[ev.de]} → ` : ''}
                      {ESTADO_INCIDENTE_LABEL[ev.para]}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(ev.em).toLocaleString('pt-BR')}
                    </Typography>
                    {ev.nota && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {ev.nota}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />
            {proximos(aberto).length === 0 ? (
              <Alert severity="info">Incidente encerrado — estado terminal.</Alert>
            ) : (
              <Stack spacing={2}>
                <TextField
                  select
                  label="Mudar para"
                  value={para}
                  onChange={(e) => setPara(e.target.value as EstadoIncidente)}
                  fullWidth
                  size="small"
                >
                  {proximos(aberto).map((e) => (
                    <MenuItem key={e} value={e}>
                      {ESTADO_INCIDENTE_LABEL[e]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Nota"
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  helperText={para === 'ENCERRADO' ? 'Obrigatória para encerrar.' : 'Fica na linha do tempo, sem edição.'}
                />
                <Button
                  variant="contained"
                  onClick={transicionar}
                  disabled={!para || transicionando || (para === 'ENCERRADO' && !nota.trim())}
                >
                  {transicionando ? 'Registrando…' : 'Registrar transição'}
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  )
}

export default PrivacidadeIncidentesPage
